import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { ICONS, ICON_TYPES } from '@/src/components/common/Elements/Icon/Icon';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';
import FileViewer from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/FileViewer/FileViewer';
import InfoPanel from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/InfoPanel';
import { useConnectedApps } from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/api/getConnectedApps';
import { TreatmentsContextProvider } from '@/src/components/project/Inbox2/components/EntryPanel/FileEntryViewer/components/InfoPanel/Treatment/store/treatments.store';
import ActionButton from '@/src/components/project/Inbox2/Elements/ActionButton/ActionButton';
import { useInbox } from '@/src/components/project/Inbox2/store/inbox.store';
import TagsManager from '@/src/components/project/Tags/TagsManager';
import { useToggle } from '@/src/hooks/useToggle';
import { RootState } from '@/src/reducers/types';
import { cn } from '@/src/util/ui';

import FileDetails from './InfoPanel/FileDetails/FileDetails';
import Treatment from './InfoPanel/Treatment/Treatment';
import TreatmentsTabHeader from './InfoPanel/Treatment/TreatmentTabHeader/TreatmentTabHeader';

import CopyFileUrl from '../../List/Body/Actions/CopyFileUrl/CopyFileUrl';
import DeleteSingle from '../../List/Body/Actions/Delete/DeleteSingle';
import Download from '../../List/Body/Actions/Download/Download';
import Move from '../../List/Body/Actions/Move/Move';

export const FileEntryViewer = ({ file, fileEntry, onClose = null }) => {
  const title = fileEntry.file.name;
  const { projectId } = useParams<{ projectId: string }>();
  const [activeTab, setActiveTab] = useState(0);
  const files = useSelector((state: RootState) => state.files);
  const loggedUser = useSelector((state: RootState) => state.loggedUser);
  const isEditor = files.access.role === 'editor' || loggedUser.data.userRole < 3;
  const inTrash = files.activeInSidebar.id === 'trash';

  const [showInfoPanel, toggleShowInfoPanel] = useToggle(false);

  const connectedAppsQuery = useConnectedApps({
    projectId,
  });
  const { isTransactionsEnabled, setIsTransactionsEnabled } = useInbox();

  useEffect(() => {
    setIsTransactionsEnabled(!!connectedAppsQuery.data?.quickbooks);
  }, [connectedAppsQuery.data, setIsTransactionsEnabled]);

  if (isTransactionsEnabled === null) {
    return <Spinner />;
  }

  const itemIdsVersioned = [{ id: file.id, version: file.version }];
  const fileEntrfileEntryWithLibraryItemId = {
    ...fileEntry,
    file: {
      ...fileEntry.file,
      id: file.id,
    },
  };

  return (
    <div
      className={cn(
        'modal-content tw-flex-grow tw-overflow-hidden',
        '!tw-rounded-[5px]',
        'tw-h-full'
      )}
    >
      {/* Modal Header */}
      <div className={cn('modal-header', 'tw-border-b-0', 'tw-rounded-t-[5px]')}>
        <div className={cn('tw-flex tw-items-center tw-justify-between')}>
          <div className="tw-flex tw-items-center tw-gap-[16px] tw-whitespace-nowrap">
            <p
              className={cn(
                'tw-flex tw-items-center',
                'tw-text-[18px] tw-font-brandonTextMedium',
                'tw-m-0',
                'tw-text-cfo-black '
              )}
            >
              {title}
            </p>
            <div className="tw-relative">
              <TagsManager
                rowId={file.id ? String(file.id) : 'file-entry-viewer'}
                isFilesTable={false}
                isLibrary={true}
                projectId={projectId}
                folderId={file.id}
                inboxDocumentId={file.id}
                assignedTags={file.tags ?? []}
                style={{ alignSelf: 'center' }}
              />
            </div>
          </div>
          <div id="documentViewerModal" className="actions">
            <div className="button-action tw-flex tw-items-center">
              {isEditor && <Move itemIdsVersioned={itemIdsVersioned} shouldResetSelected={false} />}
              <Download itemIds={[file.id]} />
              <CopyFileUrl
                projectId={projectId}
                fileId={file.id}
                sourceId={file['source-id']}
                iconOnly
              />
              {isEditor && (
                <DeleteSingle
                  file={file}
                  itemIdsVersioned={itemIdsVersioned}
                  inTrash={inTrash}
                  showLabel={false}
                />
              )}
              <ActionButton
                id={'info_document'}
                onClick={toggleShowInfoPanel}
                icon={ICONS.info}
                iconType={cn(showInfoPanel ? ICON_TYPES.filled : ICON_TYPES.outlined)}
              />

              {onClose && (
                <button
                  type="button"
                  className="text-black tw-bg-transparent tw-border-none tw-p-0"
                  data-tooltip-id={`fileAction_close`}
                  data-dismiss="modal"
                  aria-label="Close"
                  onClick={onClose}
                  tabIndex={0}
                >
                  <i className="material-icons text-size20">close</i>
                  <Tooltip id={`fileAction_close`} place="bottom" effect="solid">
                    Close
                  </Tooltip>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Body */}
      <div
        className={cn(
          'modal-body tw-h-[calc(100%-58px)] tw-p-0',
          'tw-border-solid tw-border-t-[1px] tw-border-t-cfo-grey-500'
        )}
      >
        <div className="tw-flex tw-h-full">
          <div className={cn('tw-h-full tw-w-[50%] tw-mx-auto')}>
            <FileViewer file={fileEntry.file} className="tw-h-full" />
          </div>
          {showInfoPanel && (
            <div className="tw-w-[50%] tw-h-full">
              <TreatmentsContextProvider fileId={file.id} isViewInQuickbooksEnabled={false}>
                <div
                  className={cn(
                    'container--selectedEntryDetails',
                    'tw-border-l-[1px] tw-border-l-solid tw-border-l-cfo-grey-500',
                    'tw-w-full tw-h-full tw-grow'
                  )}
                >
                  <InfoPanel
                    projectId={projectId}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    fileEntry={fileEntry}
                    FileDetailsComponent={FileDetails}
                    treatmentComponent={
                      <Treatment fileEntry={fileEntrfileEntryWithLibraryItemId} />
                    }
                    treatmentsTabHeaderComponent={<TreatmentsTabHeader documentId={file.id} />}
                  />
                </div>
              </TreatmentsContextProvider>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
